/* Get Insights — DeckorateAPI client. POSTs extracted selection content
 * (chart series, table cells, or image base64) to /v1/deckorate/content/get-insights
 * and returns the AI analysis. Full path used since deckorateApiBaseUrl carries
 * no /v1/deckorate segment; wire fields are snake_case, matching Fix
 * Alignment/Convert Infographic's FastAPI models. */

import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import type {
  InsightsContent,
  InsightsFollowUpAction,
  InsightsFollowUpOption,
  InsightsRequest,
  InsightsResponse,
  InsightsSelectionType,
} from "../../../types";
import { logConnectorFailure } from "../../../../../telemetry";

/** connectorSystemName tag for all Get Insights telemetry. */
const INSIGHTS_CONNECTOR = "deckorate";

/** The DeckorateAPI route this feature drives — its full path, since
 *  config.deckorateApiBaseUrl carries no /v1/deckorate segment here. */
const GET_INSIGHTS_PATH = "/v1/deckorate/content/get-insights";

/** How long to wait for one get-insights call before giving up on it.
 *  Mirrors Fix Alignment/Convert Infographic's 90s budget for a single AI pass. */
export const INSIGHTS_REQUEST_TIMEOUT_MS = 90_000;

/** Matches GetInsightsRequest.selection.shapes[].table_data's per-cell
 *  max_length — trimmed client-side so an oversized table cell doesn't 422 outright. */
export const MAX_TABLE_CELL_CHARS = 2000;

/** Reports a Get Insights failure through the shared telemetry pipeline
 *  (PII-scrubbed, connectorSystemName-tagged event). */
export function logInsightsError(reason: string, error: unknown): void {
  logConnectorFailure(INSIGHTS_CONNECTOR, error, reason);
}

/** Centralized so every catch site in this feature extracts messages the same way. */
export function getInsightsErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/** True when a rejection came from an AbortController rather than the server. */
export function isInsightsAbortError(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === "AbortError"
    : error instanceof Error && error.name === "AbortError";
}

/* ── Error copy ──────────────────────────────────────────────────────────────
 * Turns createHttpClient's `Deckorate POST /content/get-insights: <status>
 * <rawBody>` into panel-worthy copy: the API's own `message` when actionable,
 * plain-English copy otherwise. Mirrors describeAlignmentFailure/describeInfographicFailure.
 */
const INSIGHTS_HTTP_ERROR_PREFIX = "Deckorate ";
const INSIGHTS_HTTP_ERROR_SEPARATOR = ": ";
const INSIGHTS_LEADING_STATUS_PATTERN = /^\d{3}/;

interface ParsedInsightsHttpError {
  readonly status: number;
  readonly body: string;
}

function parseInsightsHttpError(raw: string): ParsedInsightsHttpError | null {
  if (!raw.startsWith(INSIGHTS_HTTP_ERROR_PREFIX)) return null;
  const separator = raw.indexOf(INSIGHTS_HTTP_ERROR_SEPARATOR, INSIGHTS_HTTP_ERROR_PREFIX.length);
  if (separator === -1) return null;

  const remainder = raw.slice(separator + INSIGHTS_HTTP_ERROR_SEPARATOR.length);
  const status = INSIGHTS_LEADING_STATUS_PATTERN.exec(remainder);
  if (!status) return null;

  return { status: Number(status[0]), body: remainder.slice(status[0].length).trim() };
}

const INSIGHTS_HTTP_STATUS = {
  unauthorized: 401,
  forbidden: 403,
  tooManyRequests: 429,
  serverError: 500,
} as const;

const INSIGHTS_UNAUTHORIZED_MESSAGE = "Your session has expired — sign in again and retry.";
const INSIGHTS_BUSY_MESSAGE = "The insights service is busy right now — try again in a moment.";
const INSIGHTS_SERVER_MESSAGE = "The insights service is unavailable right now. Try again shortly.";
export const INSIGHTS_TIMEOUT_MESSAGE = "The insights service took too long to respond. Try again.";
export const INSIGHTS_CANCELLED_MESSAGE = "Get Insights was cancelled.";

/** Pull `message`/`error.message` out of either error envelope DeckorateAPI can return. */
function insightsApiErrorMessage(rawBody: string): string | null {
  try {
    const parsed: unknown = JSON.parse(rawBody);
    if (typeof parsed !== "object" || parsed === null) return null;
    const body = parsed as { message?: unknown; error?: { message?: unknown } };
    if (typeof body.message === "string" && body.message) return body.message;
    const nested = body.error?.message;
    return typeof nested === "string" && nested ? nested : null;
  } catch {
    // rawBody isn't JSON (e.g. an HTML proxy error page) — not a bug, just
    // means no API message is available.
    return null;
  }
}

export function describeInsightsFailure(
  error: unknown,
  fallback: string,
  options: { timedOut?: boolean } = {},
): string {
  if (isInsightsAbortError(error)) {
    return options.timedOut ? INSIGHTS_TIMEOUT_MESSAGE : INSIGHTS_CANCELLED_MESSAGE;
  }

  const raw = getInsightsErrorMessage(error, fallback);
  const parsed = parseInsightsHttpError(raw);
  if (!parsed) return raw;

  const { status } = parsed;
  const apiMessage = insightsApiErrorMessage(parsed.body);

  if (status === INSIGHTS_HTTP_STATUS.unauthorized || status === INSIGHTS_HTTP_STATUS.forbidden) {
    return INSIGHTS_UNAUTHORIZED_MESSAGE;
  }
  if (status === INSIGHTS_HTTP_STATUS.tooManyRequests) return INSIGHTS_BUSY_MESSAGE;
  if (status >= INSIGHTS_HTTP_STATUS.serverError) return INSIGHTS_SERVER_MESSAGE;
  // 4xx (validation) — the API's message says exactly what.
  return apiMessage ?? raw;
}

/** Correlation id for tracing; omitted (not weakly substituted) on hosts
 *  without crypto.randomUUID — the API fills one in when absent. */
function newInsightsRequestId(): string | undefined {
  return globalThis.crypto?.randomUUID?.();
}

interface InsightsApiChartSeries {
  name: string;
  values: string[];
}

interface InsightsApiChartData {
  title: string;
  chart_type: string;
  categories: string[];
  series: InsightsApiChartSeries[];
}

interface InsightsApiTableShape {
  table_data: string[][];
}

/** Mirrors DeckorateAPI's SelectionContent (models/content_api/get_insights.py) —
 *  every field optional except what `source` calls for. */
interface InsightsApiSelection {
  text_content: string;
  shapes: InsightsApiTableShape[];
  chart_data: InsightsApiChartData | null;
  image_base64: string | null;
  content_type: string;
}

interface GetInsightsApiRequest {
  request_id?: string;
  source: InsightsSelectionType;
  selection: InsightsApiSelection;
}

/** Deliberately `unknown`-typed wire data, validated before use. Mirrors
 *  DeckorateAPI's GetInsightsResponse; only `insights` and `follow_up_options`
 *  are read (`result` is a markdown rendering the panel doesn't need). */
interface RawGetInsightsResponse {
  request_id?: unknown;
  insights?: unknown;
  follow_up_options?: unknown;
}

const FOLLOW_UP_ACTIONS: ReadonlySet<InsightsFollowUpAction> = new Set([
  "add_to_slide",
  "create_new_slide",
  "make_infographic",
]);

function isFollowUpAction(value: unknown): value is InsightsFollowUpAction {
  return typeof value === "string" && FOLLOW_UP_ACTIONS.has(value as InsightsFollowUpAction);
}

function toFollowUpOptions(value: unknown): InsightsFollowUpOption[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is { action: unknown; label: unknown } => typeof item === "object" && item !== null)
    .filter((item) => isFollowUpAction(item.action) && typeof item.label === "string" && item.label)
    .map((item) => ({ action: item.action as InsightsFollowUpAction, label: item.label as string }));
}

function toStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

/** Normalises a response into an InsightsResponse, dropping anything unusable.
 *  Malformed fields point at a proxy error page or truncated body — surviving
 *  with the good fields beats failing the action, but it's reported so the
 *  drift is visible. */
export function normaliseInsightsResult(raw: RawGetInsightsResponse | null | undefined): InsightsResponse {
  const body = raw ?? {};
  if (body.insights !== undefined && !Array.isArray(body.insights)) {
    logInsightsError("insights-bad-insights", new Error("insights was not an array"));
  }

  return { insights: toStringArray(body.insights), followUpOptions: toFollowUpOptions(body.follow_up_options) };
}

/** Builds DeckorateAPI's `selection` (SelectionContent) from the extracted
 *  shape content — only the field matching `content.type`/`source` is populated. */
function toApiSelection(content: InsightsContent): InsightsApiSelection {
  const base: InsightsApiSelection = {
    text_content: "",
    shapes: [],
    chart_data: null,
    image_base64: null,
    content_type: "image/png",
  };

  if (content.type === "image") {
    return { ...base, image_base64: content.base64, content_type: content.mimeType };
  }
  if (content.type === "table") {
    return {
      ...base,
      shapes: [{ table_data: content.rows.map((row) => row.map((cell) => cell.slice(0, MAX_TABLE_CELL_CHARS))) }],
    };
  }
  return {
    ...base,
    chart_data: {
      title: content.title ?? "",
      chart_type: "",
      categories: content.categories,
      series: content.series.map((series) => ({ name: series.name, values: series.values })),
    },
  };
}

/** POSTs the extracted selection content and returns the validated insights.
 *  Cancellations aren't failures — only genuine request/parse errors are
 *  reported to telemetry, mirroring postConvertInfographic. */
export async function fetchInsights(
  http: HttpClient,
  request: InsightsRequest,
  options: HttpRequestOptions = {},
): Promise<InsightsResponse> {
  const apiRequest: GetInsightsApiRequest = {
    request_id: newInsightsRequestId(),
    source: request.selectionType,
    selection: toApiSelection(request.content),
  };

  try {
    const response = await http.post<RawGetInsightsResponse | null>(GET_INSIGHTS_PATH, apiRequest, options);
    return normaliseInsightsResult(response);
  } catch (error) {
    if (!isInsightsAbortError(error)) logInsightsError("insights-request-failed", error);
    throw error;
  }
}
